import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "../select";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Sheet, SheetTrigger, SheetContent } from "../sheet";
import { Calendar } from "../calendar";
import { Button } from "../button";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import * as Icons from "./Icons";
import { CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useOpportunityFilters } from "@/hooks/useOpportunityFilters";
import { useTeam } from "@/hooks/useTeam";
import { Badge } from "../badge";
import { Input } from "../input";
import type { OpportunityStatus } from "@/hooks/useOpportunities";
import {
  CRM_DATE_FIELD_OPTIONS,
  DEFAULT_CRM_DATE_FIELD,
  getCrmDateFieldOption,
  type CrmDateField,
} from "@/lib/crmDateFilter";
import { formatDayKey } from "@/lib/accountTimezone";
import { useCurrency } from "@/hooks/useCurrency";

/** Calendar pickers hand back a local calendar day, not an instant — keep it zone-free. */
const toDayKey = (date: Date) => format(date, "yyyy-MM-dd");

export type OpportunityStatusFilter = OpportunityStatus | 'all';
export type { CrmDateField };

export interface OpportunityFilters {
  tags: string[];
  minPrice: number | null;
  maxPrice: number | null;
  dateFrom: Date | null;
  dateTo: Date | null;
  dateField: CrmDateField;
  /** auth_user_id do responsável, ou `__unassigned__` para sem atribuição */
  attributionId: string | null;
  status: OpportunityStatusFilter;
}

interface MoreFilterCRMProps {
  currentFilters?: OpportunityFilters;
  onFilterChange?: (filters: OpportunityFilters) => void;
  onClearFilters?: () => void;
}

const MoreFilterCRM: React.FC<MoreFilterCRMProps> = ({
  currentFilters = {
    tags: [],
    minPrice: null,
    maxPrice: null,
    dateFrom: null,
    dateTo: null,
    dateField: DEFAULT_CRM_DATE_FIELD,
    attributionId: null,
    status: 'open'
  },
  onFilterChange,
  onClearFilters
}) => {
  const { t } = useTranslation("common");
  const { symbol: currencySymbol } = useCurrency();
  const { uniqueTags, loading: loadingFilters } = useOpportunityFilters();
  const { members, loading: loadingMembers } = useTeam();

  const [localTag, setLocalTag] = useState<string>(currentFilters?.tags[0] || '');
  const [localMinPrice, setLocalMinPrice] = useState<string>(currentFilters?.minPrice?.toString() || '');
  const [localMaxPrice, setLocalMaxPrice] = useState<string>(currentFilters?.maxPrice?.toString() || '');
  const [localDateFrom, setLocalDateFrom] = useState<Date | undefined>(currentFilters?.dateFrom || undefined);
  const [localDateTo, setLocalDateTo] = useState<Date | undefined>(currentFilters?.dateTo || undefined);
  const [localDateField, setLocalDateField] = useState<CrmDateField>(
    currentFilters?.dateField || DEFAULT_CRM_DATE_FIELD
  );
  const [localAttributionId, setLocalAttributionId] = useState<string>(currentFilters?.attributionId || '');
  const [localStatus, setLocalStatus] = useState<OpportunityStatusFilter>(currentFilters?.status || 'open');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (currentFilters) {
      setLocalTag(currentFilters.tags[0] || '');
      setLocalMinPrice(currentFilters.minPrice?.toString() || '');
      setLocalMaxPrice(currentFilters.maxPrice?.toString() || '');
      setLocalDateFrom(currentFilters.dateFrom || undefined);
      setLocalDateTo(currentFilters.dateTo || undefined);
      setLocalDateField(currentFilters.dateField || DEFAULT_CRM_DATE_FIELD);
      setLocalAttributionId(currentFilters.attributionId || '');
      setLocalStatus(currentFilters.status || 'open');
    }
  }, [currentFilters]);

  const handleApplyFilters = () => {
    if (!onFilterChange) {
      console.warn('[MoreFilterCRM] onFilterChange nao esta definido');
      setIsOpen(false);
      return;
    }

    const attributionId =
      !localAttributionId || localAttributionId === '__clear__' ? null : localAttributionId;

    const newFilters: OpportunityFilters = {
      tags: localTag ? [localTag] : [],
      minPrice: localMinPrice ? parseFloat(localMinPrice) : null,
      maxPrice: localMaxPrice ? parseFloat(localMaxPrice) : null,
      dateFrom: localDateFrom || null,
      dateTo: localDateTo || null,
      dateField: localDateField,
      attributionId,
      status: localStatus,
    };

    onFilterChange(newFilters);
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    setLocalTag('');
    setLocalMinPrice('');
    setLocalMaxPrice('');
    setLocalDateFrom(undefined);
    setLocalDateTo(undefined);
    setLocalDateField(DEFAULT_CRM_DATE_FIELD);
    setLocalAttributionId('');
    setLocalStatus('open');

    if (onClearFilters) {
      onClearFilters();
    }
    setIsOpen(false);
  };

  const dateFieldOption = getCrmDateFieldOption(localDateField);
  const attributionOptions = members.filter((member) => member.auth_user_id && member.is_active);

  const activeFiltersCount = currentFilters ? [
    currentFilters.tags.length > 0,
    currentFilters.minPrice !== null,
    currentFilters.maxPrice !== null,
    currentFilters.dateFrom,
    currentFilters.dateTo,
    currentFilters.dateField !== DEFAULT_CRM_DATE_FIELD,
    !!currentFilters.attributionId,
    currentFilters.status !== 'open'
  ].filter(Boolean).length : 0;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger>
        <div className="border min-h-[40px] px-3 border-border bg-white hover:bg-[#F8FAFC] transition-colors duration-200 rounded-[12px] flex justify-center items-center gap-2 relative shadow-sm dark:border-neutral-500 dark:bg-neutral-600 dark:hover:bg-neutral-500 dark:rounded-[6px] dark:shadow-none">
          <p className="text-foreground font-medium text-[14px] dark:text-neutral-50">
            {t("shared.moreFilters")}
          </p>
          <Icons.filter className="w-[16px] h-[16px] text-muted-foreground dark:text-neutral-50" />
          {activeFiltersCount > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center bg-[#2563EB] hover:bg-[#2563EB] text-white text-xs">
              {activeFiltersCount}
            </Badge>
          )}
        </div>
      </SheetTrigger>
      <SheetContent className="bg-card border-border p-3 flex flex-col gap-4 dark:bg-[#18181B] dark:border-[#3F3F464D]">
        <div className="w-full gap-2 flex justify-center items-center">
          <p className="text-[16px] font-medium text-foreground dark:text-neutral-100">
            {t("shared.moreFilters")}
          </p>
          <Icons.filter className="w-[16px] h-[16px] text-muted-foreground dark:text-neutral-100" />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-muted-foreground dark:text-neutral-300">{t("shared.status")}</label>
          <Select value={localStatus} onValueChange={(v) => setLocalStatus(v as OpportunityStatusFilter)}>
            <SelectTrigger className="w-full !text-foreground bg-white border-border dark:!text-neutral-50 dark:bg-transparent dark:border-[#3F3F464D]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="open">{t("shared.statusOpen")}</SelectItem>
                <SelectItem value="won">{t("shared.statusWon")}</SelectItem>
                <SelectItem value="lost">{t("shared.statusLost")}</SelectItem>
                <SelectItem value="all">{t("shared.all")}</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-muted-foreground dark:text-neutral-300">{t("shared.dateField")}</label>
          <Select
            value={localDateField}
            onValueChange={(v) => setLocalDateField(v as CrmDateField)}
          >
            <SelectTrigger className="w-full !text-foreground bg-white border-border dark:!text-neutral-50 dark:bg-transparent dark:border-[#3F3F464D]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {CRM_DATE_FIELD_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-muted-foreground dark:text-neutral-300">{t("shared.assignedTo")}</label>
          <Select value={localAttributionId || '__clear__'} onValueChange={setLocalAttributionId}>
            <SelectTrigger className="w-full !text-foreground bg-white border-border dark:!text-neutral-50 dark:bg-transparent dark:border-[#3F3F464D]">
              <SelectValue
                placeholder={
                  loadingMembers
                    ? t('shared.loading')
                    : attributionOptions.length === 0
                      ? t('shared.noOwnerFound')
                      : t('shared.pickOwner')
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__clear__">{t("shared.all")}</SelectItem>
                <SelectItem value="__unassigned__">{t("shared.unassigned")}</SelectItem>
                {attributionOptions.map((member) => (
                  <SelectItem key={member.auth_user_id} value={member.auth_user_id!}>
                    {member.name || member.email}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-muted-foreground dark:text-neutral-300">{t("shared.tags")}</label>
          <Select value={localTag} onValueChange={setLocalTag}>
            <SelectTrigger className="w-full !text-foreground bg-white border-border dark:!text-neutral-50 dark:bg-transparent dark:border-[#3F3F464D]">
              <SelectValue placeholder={loadingFilters ? t("shared.loading") : uniqueTags.length === 0 ? t("shared.noTagFound") : t("shared.pickTag")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__clear__">{t("shared.allF")}</SelectItem>
                {uniqueTags.map((tag) => (
                  <SelectItem key={tag.name} value={tag.name}>
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-muted-foreground dark:text-neutral-300">{t("shared.minValue")} ({currencySymbol})</label>
          <Input
            type="number"
            value={localMinPrice}
            onChange={(e) => setLocalMinPrice(e.target.value)}
            placeholder="0,00"
            className="w-full !text-foreground bg-white border-border dark:!text-neutral-50 dark:bg-[#18181B] dark:border-[#3F3F464D]"
            min="0"
            step="0.01"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-muted-foreground dark:text-neutral-300">{t("shared.maxValue")} ({currencySymbol})</label>
          <Input
            type="number"
            value={localMaxPrice}
            onChange={(e) => setLocalMaxPrice(e.target.value)}
            placeholder="0,00"
            className="w-full !text-foreground bg-white border-border dark:!text-neutral-50 dark:bg-[#18181B] dark:border-[#3F3F464D]"
            min="0"
            step="0.01"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-muted-foreground dark:text-neutral-300">
            {t(dateFieldOption.fromLabelKey)}
          </label>
          <Popover>
            <PopoverTrigger
              className="!w-full !min-h-[56px] text-[14px] font-medium !text-foreground border border-border !bg-white dark:!text-neutral-50 dark:border-[#3F3F464D] dark:!bg-[#18181B]"
              asChild
            >
              <Button
                variant={"outline"}
                className={cn(
                  "w-full flex justify-between text-left font-normal",
                  !localDateFrom && "text-muted-foreground"
                )}
              >
                {localDateFrom ? formatDayKey(toDayKey(localDateFrom), { dateStyle: 'long' }) : <span>{t("shared.pickDate")}</span>}
                <CalendarIcon />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={localDateFrom}
                onSelect={setLocalDateFrom}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-muted-foreground dark:text-neutral-300">
            {t(dateFieldOption.toLabelKey)}
          </label>
          <Popover>
            <PopoverTrigger
              className="!w-full !min-h-[56px] text-[14px] font-medium !text-foreground border border-border !bg-white dark:!text-neutral-50 dark:border-[#3F3F464D] dark:!bg-[#18181B]"
              asChild
            >
              <Button
                variant={"outline"}
                className={cn(
                  "w-full flex justify-between text-left font-normal",
                  !localDateTo && "text-muted-foreground"
                )}
              >
                {localDateTo ? formatDayKey(toDayKey(localDateTo), { dateStyle: 'long' }) : <span>{t("shared.pickDate")}</span>}
                <CalendarIcon />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={localDateTo}
                onSelect={setLocalDateTo}
                initialFocus
                disabled={(date) => localDateFrom ? date < localDateFrom : false}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="w-full flex gap-2 mt-auto">
          <Button
            onClick={handleClearFilters}
            variant="outline"
            className="flex-1 border-border bg-transparent hover:bg-[#F1F5F9] text-foreground dark:border-neutral-500 dark:hover:bg-neutral-700 dark:text-neutral-50"
          >
            <X className="w-4 h-4 mr-2" />
            {t("shared.clear")}
          </Button>
          <Button onClick={handleApplyFilters} className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
            Filtrar
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MoreFilterCRM;
